import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { timezone } from "@/utils";
import { donorSchema, userSchema } from "@/utils/FormValidationSchema";
import { useAppSelector } from "@/hooks";

const useAnnualMembershipForm = ({
  isGift,
  defaultPackageSlug,
  urlsArguments,
  options,
}) => {
  const { promoCodeSuccess: isPromoCodeApplied } = useAppSelector(
    (state) => state.landingPage
  );
  const { countryCodeSuccess, countryCodeData } = useAppSelector(
    (state) => state.dashboard
  );
  const userDefaultValue = useMemo(
    () => ({
      name: "",
      mobileCountryCode: countryCodeData?.default?.code || "+91",
      mobile: "",
      email: "",
      goal: "",
      promoCode: "",
    }),
    [countryCodeSuccess]
  );

  const donorDefaultValue = useMemo(
    () => ({
      donorName: "",
      donorEmail: "",
      donorMobileCountryCode: countryCodeData?.default?.code || "+91",
      donorMobile: "",
    }),
    [countryCodeSuccess]
  );

  const resolver = useMemo(
    () => yupResolver(isGift ? userSchema.concat(donorSchema) : userSchema),
    [isGift]
  );

  const methods = useForm({
    resolver,
    defaultValues: {
      ...userDefaultValue,
      ...donorDefaultValue,
    },
    mode: "onBlur",
  });

  const { reset, getValues } = methods;

  const resetValuesOnToggle = useCallback(() => {
    reset(
      {
        ...getValues(),
        ...donorDefaultValue,
      },
      { keepErrors: true, keepDirty: true }
    );
  }, [reset, getValues, donorDefaultValue]);

  const mapUserData = useCallback(() => {
    const {
      name,
      mobileCountryCode,
      mobile,
      email,
      goal,
      donorName,
      donorMobileCountryCode,
      donorMobile,
      donorEmail,
      promoCode,
    } = getValues();
    const pack = goal || defaultPackageSlug;

    const goalId = options?.find((o) => o.value === goal)?.goalId;

    return {
      ...(name && { name: name }),
      ...(mobileCountryCode && {
        phone_prefix: mobileCountryCode,
      }),
      ...(mobile && { mobile: mobile }),
      ...(email && { email: email }),
      ...(pack && { package_slug: pack }),
      ...(urlsArguments && { urls_arguments: urlsArguments }),
      ...(isGift && {
        is_gift: true,
        donor_data: {
          ...(donorName && { name: donorName }),
          ...(donorMobileCountryCode && {
            phone_prefix: donorMobileCountryCode,
          }),
          ...(donorMobile && { mobile: donorMobile }),
          ...(donorEmail && { email: donorEmail }),
        },
      }),
      ...(isPromoCodeApplied && {
        coupon_code: promoCode?.toUpperCase() || "",
      }),
      ...(goalId && {
        nutrition_goal_id: goalId,
      }),
      timezone,
    };
  }, [
    getValues,
    defaultPackageSlug,
    urlsArguments,
    isGift,
    isPromoCodeApplied,
    options,
  ]);

  return { methods, resetValuesOnToggle, mapUserData };
};

export default useAnnualMembershipForm;
